/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import Table, { TableColumn } from '../../components/table/Table';
import LoadingPlaceholder from '../../components/LoaderContainer/LoadingPlaceholder';
import { fmtDate, isSponsorshipActive, SponsoredCompanyRow } from './utils';
import { FormGroup, Input } from 'reactstrap';
import { addSponsorship, cancelSponsorship, refreshSubscriptions } from '../../actions/portfolio-tracker';
import { useAppDispatch } from '../../reducers';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { BlockingLoader } from '../../components/loader/BlockingLoader';
import IconButton from '../../components/button/IconButton';
import { CompanySubscriptionHistoryModal } from './partials/CompanySubscriptionHistoryModal';
import { DATE } from '../../utils/date';
import SimpleTooltip from '../../components/simple-tooltip';

interface CompanyStateInterface {
  rows: SponsoredCompanyRow[];
  loaded: boolean;
};

export interface CompanyTableProps {
  portfolioId: string;
  handleReload: () => Promise<void>;
  companiesState: CompanyStateInterface;
  referralCode: string;
};

export const CompanyTable = ({ referralCode, portfolioId, companiesState, handleReload }: CompanyTableProps) => {

  const dispatch = useAppDispatch();

  const [isLoading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState<SponsoredCompanyRow | undefined>();

  const numberSort = (a: number, b: number) => a > b ? 1 : a < b ? -1 : 0;

  const showError = (e: any) => {
    dispatch(addSiteAlert({
      timeout: 10000,
      color: SiteAlertColors.Danger,
      content: e.message ?? 'There was an error trying to complete your request'
    }));
  }

  const handleToggle = async (company: SponsoredCompanyRow) => {
    setLoading(true);
    if (isSponsorshipActive(company)) {
      await cancelSponsorship(portfolioId, referralCode, company._id).catch(showError);
    } else {
      await addSponsorship(portfolioId, referralCode, company._id).catch(showError);
    }
    await handleReload();
    setLoading(false);
  }

  const handleRefresh = async (initiativeId: string) => {
    await refreshSubscriptions(portfolioId, initiativeId).catch(showError);
    await handleReload();
  }

  const tableColumns: TableColumn<SponsoredCompanyRow>[] = [
    {
      id: 'toggle',
      Header: () => null,
      Cell: (c) => (
        <SimpleTooltip text={!c.row.original.canBeSponsored ? 'This company cannot be sponsored because they have an upgraded subscription. Please contact support for more info.' : undefined}>
          <FormGroup switch>
            <Input
              type='switch' role='switch'
              defaultChecked={isSponsorshipActive(c.row.original)}
              onChange={() => handleToggle(c.row.original)}
              disabled={!c.row.original.canBeSponsored}
            />
          </FormGroup>
        </SimpleTooltip>
      )
    },
    {
      accessor: 'name',
      Header: () => 'Name',
      Cell: (c) => c.value ?? null
    },
    {
      accessor: 'status',
      Header: () => 'Status',
      Cell: (c) => !c.row.original.canBeSponsored ? 'upgraded' : c.value
    },
    {
      accessor: 'discountStart',
      Header: () => 'Sponsorship Start',
      cellProps: {
        className: 'text-right'
      },
      sortType: (a, b) => numberSort(a.original.discountStart, b.original.discountStart),
      Cell: (c) => !c.row.original.canBeSponsored ? null : fmtDate(c.value)
    },
    {
      accessor: 'discountEnd',
      Header: () => 'Sponsorship End',
      cellProps: {
        className: 'text-right'
      },
      sortType: (a, b) => numberSort(a.original.discountEnd ?? 0, b.original.discountEnd ?? 0),
      Cell: (c) => !c.row.original.canBeSponsored ? null : fmtDate(c.value, DATE.HUMANIZE)
    },
    {
      accessor: 'periodEnd',
      Header: () => 'Next Renewal',
      cellProps: {
        className: 'text-right'
      },
      sortType: (a, b) => numberSort(a.original.periodEnd, b.original.periodEnd),
      Cell: (c) => !c.row.original.canBeSponsored ? null : fmtDate(c.value, DATE.HUMANIZE)
    },
    {
      id: 'links',
      accessor: 'subscriptions',
      Header: () => null,
      cellProps: {
        className: 'text-nowrap'
      },
      Cell: (c) => (
        <>
          <IconButton icon='fal fa-arrows-rotate' color='transparent' outline={false} onClick={() => handleRefresh(c.row.original._id)} />
          {!c.row.original.canBeSponsored ? null : <IconButton icon='fal fa-eye' color='transparent' outline={false} onClick={() => setShowDetails(c.row.original)} />}
        </>
      )
    },
  ];

  const isLoaded = companiesState.loaded === true;
  const noResults = companiesState.rows.length === 0;
  let noCompanysMessage = 'You have no companies sponsored using this referral code';

  return (
    <>
      {isLoading ? <BlockingLoader /> : null}
      <LoadingPlaceholder isLoading={!isLoaded} height={40} />
      <LoadingPlaceholder isLoading={!isLoaded} height={40} count={5} className='mt-1 background-ThemeTextWhite'>
        {noResults
          ?
          <div className='alert alert-primary'>{noCompanysMessage}</div>
          :
          <div className='company-table-container'>
            <Table columns={tableColumns} data={companiesState.rows} pageSize={25} />
          </div>
        }
        {showDetails ? <CompanySubscriptionHistoryModal {...showDetails} toggle={() => setShowDetails(undefined)} /> : null}
      </LoadingPlaceholder>
    </>
  )
}
